import type { Value } from '@fctg-corporate/smartscript-core';
import type { OktaAuthOptions } from '@okta/okta-auth-js';

const DEFAULT_TOKEN_NAME = 'default_access_token';
const FF_PROTAS_TOKEN_NAME = 'protas_access_token';
const FILE_FINISHER_TOKEN_NAME = 'file_finisher_d365_access_token';
const PS_DYNAMICS_TOKEN_NAME = 'passive_segments_dynamics_access_token';
const RAIL_D365_COMPANY_API_TOKEN_NAME = 'rail_D365_company_api_access_token';
const RAIL_D365_SMARTSCRIPT_API_TOKEN_NAME = 'rail_D365_smartscript_api_access_token';

const REDIRECT_URI_COMPANY_API = import.meta.env.VITE_REDIRECT_URI_COMPANY_API;
const REDIRECT_URI_SMARTSCRIPT_API = import.meta.env.VITE_REDIRECT_URI_SMARTSCRIPT_API;

const PRODUCTIVE_OPS_ISSUER = import.meta.env.VITE_ISSUER_PRODUCTIVE_OPS;
const SMARTSCRIPT_API_ISSUER = import.meta.env.VITE_ISSUER_SMARTSCRIPT_API;

const COMPANY_API_SCOPE = import.meta.env.VITE_PS_COMPANY_API_SCOPE;
const SMSC_API_SCOPE = import.meta.env.VITE_SMARTSCRIPT_API_SCOPE;
const PROFILE_SCOPE = import.meta.env.VITE_PROFILE_SCOPE;

export type SmartscriptConfig = {
	tokenName: string;
	issuer: string;
	scopes: string[];
	redirectUri: string;
	path: string
};

export const SMARTSCRIPT_CONFIGS: Record<string, SmartscriptConfig[]> = {
	'File Finishing Protas': [
		{
			tokenName: FF_PROTAS_TOKEN_NAME,
			issuer: PRODUCTIVE_OPS_ISSUER,
			scopes: [COMPANY_API_SCOPE],
			redirectUri: REDIRECT_URI_COMPANY_API,
			path: '/company-api',
		},
	],
	'File Finisher D365': [
		{
			tokenName: FILE_FINISHER_TOKEN_NAME,
			issuer: PRODUCTIVE_OPS_ISSUER,
			scopes: [COMPANY_API_SCOPE, PROFILE_SCOPE],
			redirectUri: REDIRECT_URI_COMPANY_API,
			path: '/company-api',
		},
	],
	'Passive Segments Dynamics': [
		{
			tokenName: PS_DYNAMICS_TOKEN_NAME,
			issuer: SMARTSCRIPT_API_ISSUER,
			scopes: [SMSC_API_SCOPE],
			redirectUri: REDIRECT_URI_SMARTSCRIPT_API,
			path: '/smartscript-api',
		},
	],
	'Rail D365': [
		{
			tokenName: RAIL_D365_COMPANY_API_TOKEN_NAME,
			issuer: PRODUCTIVE_OPS_ISSUER,
			scopes: [COMPANY_API_SCOPE],
			redirectUri: REDIRECT_URI_COMPANY_API,
			path: '/company-api',
		},
		{
			tokenName: RAIL_D365_SMARTSCRIPT_API_TOKEN_NAME,
			issuer: SMARTSCRIPT_API_ISSUER,
			scopes: [SMSC_API_SCOPE],
			redirectUri: REDIRECT_URI_SMARTSCRIPT_API,
			path: '/smartscript-api',
		},
	],
	'default': [
		{
			tokenName: DEFAULT_TOKEN_NAME,
			issuer: PRODUCTIVE_OPS_ISSUER,
			scopes: [],
			redirectUri: REDIRECT_URI_COMPANY_API,
			path: '/company-api',
		},
	],
};

export const getSmartscriptConfig = (smartscriptName: Value): SmartscriptConfig[] => {
	if (!smartscriptName || !SMARTSCRIPT_CONFIGS[smartscriptName]) {
		const fallback = localStorage.getItem('smartscript');

		return SMARTSCRIPT_CONFIGS[fallback || 'default'];
	}

	return SMARTSCRIPT_CONFIGS[smartscriptName];
};

export const getOktaAuthConfig = ({ scopes, issuer, redirectUri }: SmartscriptConfig): OktaAuthOptions => ({
	pkce: true,
	scopes: ['openid', ...scopes],
	clientId: import.meta.env.VITE_CLIENT_ID,
	issuer: issuer,
	redirectUri: redirectUri,
	tokenManager: {
		storage: 'localStorage',
		autoRenew: false,
		autoRemove: false,
	},
	storageManager: {
		token: {
			storageTypes: ['localStorage'],
		},
	},
});
